import { VStack, OptionRow, Text, Card } from 'components/ui';
import { Diagnosis, DiagnosisCategory } from '../../api/types';
import { useFormData } from 'utils/formReducer.js';

const DX_CATEGORY_ORDER = [
  DiagnosisCategory.IBD,
  DiagnosisCategory.UpperGI,
  DiagnosisCategory.Other,
];

type Props = {
  diagnoses?: Diagnosis[];
  expandIbs: boolean;
  selectDiagnoses: (ids: string[]) => void;
  setExpandIbs: (expand: boolean) => void;
};

const CoreDiagnosisSelectionForm = ({
  diagnoses,
  expandIbs,
  selectDiagnoses,
  setExpandIbs,
}: Props) => {
  const { state: formData } = useFormData();

  const ibsDiagnoses =
    diagnoses?.filter((item) => item?.category === DiagnosisCategory.IBS) || [];


  const categoryOrderIndex = (category: DiagnosisCategory) => {
    const index = DX_CATEGORY_ORDER.indexOf(category);

    return index === -1 ? DX_CATEGORY_ORDER.length : index;
  };

  // exclude ibs diagnoses and order by category: ibd, upper-gi, other
  const notIbsDiagnoses = (
    diagnoses?.filter((item) => item?.category !== DiagnosisCategory.IBS) || []
  ).sort(
    (a, b) => categoryOrderIndex(a.category) - categoryOrderIndex(b.category)
  );

  const toggleDiagnosis = (id: string, dx_cat: DiagnosisCategory) => {
    // true if not already selected
    const selecting = !formData.diagnosis_ids.includes(id);

    // get all ids of category
    const categoryIds = diagnoses
      ?.filter(
        ({ category }: { category: DiagnosisCategory }) => category === dx_cat
      )
      ?.map(({ id }: { id: string }) => id);

    // remove all ids of category
    let selectedIds = formData.diagnosis_ids.filter(
      (dx_id: string) => !categoryIds?.includes(dx_id)
    );

    // if selecting, add id to selected id list
    if (selecting) {
      selectedIds.push(id);
    }

    selectDiagnoses(selectedIds);
  };

  const toggleIbs = (expand: boolean) => {
    if (!expand) {
      // deselect all ibs diagnoses if collapsing ibs options
      selectDiagnoses([
        ...formData.diagnosis_ids.filter(
          (dx_id: string) => !ibsDiagnoses?.map((d) => d.id)?.includes(dx_id)
        ),
      ]);
    }

    setExpandIbs(expand);
  };

  return (
    <VStack
      gap="22px"
      w="100%"
      align="stretch"
      className="personal-info-container"
    >
      <VStack gap="10px" w="100%" align="stretch" className="form-container">
        <Text textStyle="label.section" textAlign="left">
          Select all that apply.
        </Text>

        <VStack gap={2} w="100%">
          <OptionRow
            isSelected={expandIbs}
            onClick={() => toggleIbs(!expandIbs)}
          >
            Irritable Bowel Syndrome (IBS)
          </OptionRow>

          {expandIbs && (
            <Card bg={'rgb(228, 238, 241)'} p={3.5} borderRadius={'xl'}>
              <Text textStyle="label.section" textAlign="left">
                Which type of IBS?
              </Text>
              <VStack gap={2} mt={3}>
                {ibsDiagnoses?.map(({ id, diagnosis, category }) => {
                  const isSelected = formData.diagnosis_ids.includes(id);

                  return (
                    <OptionRow
                      key={id}
                      p={3}
                      isSelected={isSelected}
                      onClick={() => toggleDiagnosis(id, category)}
                    >
                      {diagnosis}
                    </OptionRow>
                  );
                })}
              </VStack>
            </Card>
          )}
          {notIbsDiagnoses?.map(({ id, diagnosis, category }) => {
            const isSelected = formData.diagnosis_ids.includes(id);

            return (
              <OptionRow
                key={id}
                isSelected={isSelected}
                onClick={() => toggleDiagnosis(id, category)}
              >
                {diagnosis}
              </OptionRow>
            );
          })}
        </VStack>
      </VStack>
    </VStack>
  );
};

export default CoreDiagnosisSelectionForm;
